/**
 * SecureStorage.ts
 *
 * ARCHITECTURE: Centralized storage abstraction for auth/user context
 *
 * PROBLEM SOLVED:
 * - Before: 50+ files directly accessing localStorage (scattered, inconsistent, unmaintainable)
 * - After: Single point of control for all auth storage operations
 *
 * BENEFITS:
 * - ✅ Type-safe access (no string typos)
 * - ✅ Single point to add encryption/obfuscation
 * - ✅ Easy migration to httpOnly cookies later
 * - ✅ Centralized token lifecycle management
 * - ✅ Mockable for testing
 * - ✅ Can log access for security auditing
 *
 * USAGE:
 *   import { SecureStorage } from '../utils/SecureStorage';
 *
 *   const token = SecureStorage.getAccessToken();
 *   SecureStorage.setUserContext({ pfuserUuid, pfuserId, username });
 *   SecureStorage.clearAuth();
 *
 * ENFORCEMENT:
 * - Pre-commit validator blocks direct localStorage.getItem('access_token')
 * - All auth storage MUST go through SecureStorage
 *
 * Created: May 11, 2026
 * Related: ARCHITECTURAL_PATTERNS_CANDIDATE_ENFORCEMENT.md
 */

// ─────────────────────────────────────────────────────────────
// Storage Keys (Single Source of Truth)
// ─────────────────────────────────────────────────────────────

const STORAGE_KEYS = {
    // JWT Authentication
    ACCESS_TOKEN: 'access_token',
    REFRESH_TOKEN: 'refresh_token',

    // User Identity
    PFUSER_UUID: 'pfuser_uuid', // Primary user UUID (database FK)
    USER_UUID: 'user_uuid', // Legacy alias (some endpoints use this)
    PFUSER_ID: 'pfuser_id', // Integer ID (legacy compatibility)
    USERNAME: 'username', // Display name

    // Session Context
    USER_SESSION: 'user_session', // Serialized session data (JSON)

    // Tenant Context (Multi-tenant isolation)
    ORGANIZATION_ID: 'organization_id', // Current org context
    TTP_ID: 'ttp_id', // Tenant Technology Platform ID

    // Navigation State
    POST_LOGIN_REDIRECT: 'post_login_redirect', // Where to go after login
} as const;

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface UserContext {
    pfuserUuid: string;
    pfuserId?: number;
    username?: string;
    userUuid?: string; // Legacy compatibility
}

export interface TenantContext {
    organizationId?: string;
    ttpId?: number;
}

export interface UserSession {
    [key: string]: any; // Flexible session data structure
}

// ─────────────────────────────────────────────────────────────
// SecureStorage Class (Static Methods Only)
// ─────────────────────────────────────────────────────────────

export class SecureStorage {
    // ─── JWT Token Management ───────────────────────────────────

    static getAccessToken(): string | null {
        return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    }

    static setAccessToken(token: string): void {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
    }

    static getRefreshToken(): string | null {
        return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    }

    static setRefreshToken(token: string): void {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
    }

    static clearTokens(): void {
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }

    // ─── User Identity Management ──────────────────────────────

    static getPfuserUuid(): string | null {
        return localStorage.getItem(STORAGE_KEYS.PFUSER_UUID);
    }

    static setPfuserUuid(uuid: string): void {
        localStorage.setItem(STORAGE_KEYS.PFUSER_UUID, uuid);
    }

    static getUserUuid(): string | null {
        return localStorage.getItem(STORAGE_KEYS.USER_UUID);
    }

    static setUserUuid(uuid: string): void {
        localStorage.setItem(STORAGE_KEYS.USER_UUID, uuid);
    }

    static getPfuserId(): number | null {
        const id = localStorage.getItem(STORAGE_KEYS.PFUSER_ID);
        return id ? parseInt(id, 10) : null;
    }

    static setPfuserId(id: number): void {
        localStorage.setItem(STORAGE_KEYS.PFUSER_ID, id.toString());
    }

    static getUsername(): string | null {
        return localStorage.getItem(STORAGE_KEYS.USERNAME);
    }

    static setUsername(username: string): void {
        localStorage.setItem(STORAGE_KEYS.USERNAME, username);
    }

    /**
     * Bulk set user context (convenience method for login)
     */
    static setUserContext(context: UserContext): void {
        this.setPfuserUuid(context.pfuserUuid);

        if (context.pfuserId !== undefined) {
            this.setPfuserId(context.pfuserId);
        }

        if (context.username) {
            this.setUsername(context.username);
        }

        if (context.userUuid) {
            this.setUserUuid(context.userUuid);
        }
    }

    /**
     * Clear all user identity data
     */
    static clearUserContext(): void {
        localStorage.removeItem(STORAGE_KEYS.PFUSER_UUID);
        localStorage.removeItem(STORAGE_KEYS.USER_UUID);
        localStorage.removeItem(STORAGE_KEYS.PFUSER_ID);
        localStorage.removeItem(STORAGE_KEYS.USERNAME);
    }

    // ─── Session Management ────────────────────────────────────

    static getUserSession(): UserSession | null {
        const sessionStr = localStorage.getItem(STORAGE_KEYS.USER_SESSION);
        if (!sessionStr) return null;

        try {
            return JSON.parse(sessionStr);
        } catch (error) {
            console.error('Failed to parse user session:', error);
            return null;
        }
    }

    static setUserSession(session: UserSession): void {
        localStorage.setItem(STORAGE_KEYS.USER_SESSION, JSON.stringify(session));
    }

    static clearUserSession(): void {
        localStorage.removeItem(STORAGE_KEYS.USER_SESSION);
    }

    // ─── Tenant Context (Multi-tenant Isolation) ───────────────

    static getOrganizationId(): string | null {
        return localStorage.getItem(STORAGE_KEYS.ORGANIZATION_ID);
    }

    static setOrganizationId(orgId: string): void {
        localStorage.setItem(STORAGE_KEYS.ORGANIZATION_ID, orgId);
    }

    static getTtpId(): number | null {
        const id = localStorage.getItem(STORAGE_KEYS.TTP_ID);
        return id ? parseInt(id, 10) : null;
    }

    static setTtpId(ttpId: number): void {
        localStorage.setItem(STORAGE_KEYS.TTP_ID, ttpId.toString());
    }

    static setTenantContext(context: TenantContext): void {
        if (context.organizationId) {
            this.setOrganizationId(context.organizationId);
        }

        if (context.ttpId !== undefined) {
            this.setTtpId(context.ttpId);
        }
    }

    static clearTenantContext(): void {
        localStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ID);
        localStorage.removeItem(STORAGE_KEYS.TTP_ID);
    }

    // ─── Navigation State ──────────────────────────────────────

    static getPostLoginRedirect(): string | null {
        return localStorage.getItem(STORAGE_KEYS.POST_LOGIN_REDIRECT);
    }

    static setPostLoginRedirect(path: string): void {
        localStorage.setItem(STORAGE_KEYS.POST_LOGIN_REDIRECT, path);
    }

    static clearPostLoginRedirect(): void {
        localStorage.removeItem(STORAGE_KEYS.POST_LOGIN_REDIRECT);
    }

    // ─── Bulk Operations (Login/Logout) ────────────────────────

    /**
     * Complete logout - clears ALL auth/user state
     */
    static clearAuth(): void {
        this.clearTokens();
        this.clearUserContext();
        this.clearUserSession();
        this.clearTenantContext();
        this.clearPostLoginRedirect();
    }

    /**
     * Check if user is authenticated (has access token)
     */
    static isAuthenticated(): boolean {
        return this.getAccessToken() !== null;
    }

    /**
     * Get all current storage keys (debugging/audit)
     */
    static getAllKeys(): string[] {
        return Object.values(STORAGE_KEYS);
    }

    /**
     * Export current auth state (for debugging)
     */
    static exportState(): Record<string, string | null> {
        const state: Record<string, string | null> = {};

        for (const key of Object.values(STORAGE_KEYS)) {
            state[key] = localStorage.getItem(key);
        }

        return state;
    }
}

// ─────────────────────────────────────────────────────────────
// Default Export (for convenience)
// ─────────────────────────────────────────────────────────────

export default SecureStorage;
